// REST /api/canvases/:canvasId からキャンバススナップショットを取得して store にロードする。
// 失敗時（404 で実体未作成）は空キャンバスとしてストアを初期化する。

import { useEffect, useState } from "react";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasApi } from "../../lib/canvas-api";
import { clearTransientForCommittedOp } from "./clear-transient-for-committed-op";

interface CanvasBootstrapState {
  readonly status: "loading" | "ready" | "error";
}

interface CanvasBootstrapOptions {
  /**
   * 取得失敗（404 の未作成に限らず 401/403/500 やネットワーク失敗を含む）時に、空キャンバスとして
   * 読み込み成功扱いにするか。通常編集画面は lazy create（初回 op でサーバ実体化）のため true が既定。
   * 参照専用の埋め込みでは false を渡し、取得失敗を「空キャンバスの成功」に変換せず error として扱う
   * （権限切れ・未存在・トークン失効を空キャンバスとして見せない。埋め込み側で失敗表示へ遷移させる）。
   */
  readonly lazyCreateOnError?: boolean | undefined;
  /**
   * 未作成キャンバスの lazy create 時に採用する初期キャンバス名（URL クエリ ?name= 由来。
   * Connect Note の「新しいキャンバス」から記事タイトルを引き継ぐ用途）。既存キャンバスを開くときは無視される。
   */
  readonly createName?: string | undefined;
}

export function useCanvasBootstrap(
  canvasId: string,
  options?: CanvasBootstrapOptions,
): CanvasBootstrapState {
  const lazyCreateOnError = options?.lazyCreateOnError ?? true;
  const createName = options?.createName;
  const canvasApi = useCanvasApi();
  const loadSnapshot = useCanvasDocStore((s) => s.loadSnapshot);
  const [status, setStatus] = useState<CanvasBootstrapState["status"]>("loading");

  // キャンバスから離脱するとき（アンマウント／canvasId 変更）に doc store を初期化する。CanvasView は
  // useCanvasBootstrap と useRealtimeConnection を同時起動し、doc store はグローバル（画面遷移をまたいで
  // 残る）ため、離脱時に初期化しないと前キャンバスの状態（canvasId / serverSeq / pendingCommits 等）が
  // 次のキャンバスへ持ち越される。特に、前キャンバスの初回 REST 取得が終わる前（store.canvasId がまだ
  // null）に op.commit だけ先着して pendingCommits に積まれた状態で離脱すると、その pending が次キャンバスの
  // loadSnapshot 初回 carry-over（state.canvasId===null 分岐）で誤って引き継がれてしまう。
  // 本アプリのキャンバス遷移はダッシュボード経由の unmount→remount が主経路で、マウント時点の判定
  // （前回 canvasId の ref 等）は再マウントで初期化されて効かないため、離脱時（cleanup）に初期化する。
  // これで次キャンバスは常にクリーンな store（canvasId=null・pending 空）から bootstrap され、
  // 初回 carry-over が拾う pending は必ず現キャンバス宛てになる。
  // クリーンアップは useRealtimeConnection の cleanup（onMessage 購読解除・切断）と同じアンマウント
  // バッチで走るため、初期化後に前キャンバスの op.commit が紛れ込むことはない。
  useEffect(() => {
    return () => {
      useCanvasDocStore.getState().reset();
    };
  }, [canvasId]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    void (async () => {
      try {
        const data = await canvasApi.fetchCanvasBootstrap(canvasId, createName);
        if (cancelled) return;
        const applied = loadSnapshot({
          canvasId: data.canvasId,
          serverSeq: data.serverSeq,
          objects: [...data.objects],
          meta: data.meta,
        });
        // REST bootstrap が snapshot 経由で committed 化した pending op（WebSocket 側が初回 fetch 完了前に
        // 順不同で先着させた高 serverSeq commit 等）の操作中ゴースト（transient）を破棄する。これをしないと
        // 確定済みオブジェクトと他参加者のゴーストが CanvasRemotePreviewsLayer の prune 保険まで二重表示される。
        applied.forEach(clearTransientForCommittedOp);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        if (lazyCreateOnError) {
          // 通常編集画面: 例外時もローカルでは空のスナップショットでスタートし、
          // 最初の op を送ったタイミングでサーバ側に実体化される設計（lazy create）。
          console.warn("[bootstrap] 取得失敗、空キャンバスで開始します", e);
          const applied = loadSnapshot({ canvasId, serverSeq: 0, objects: [] });
          // 通常は空（serverSeq 0）だが、契約統一のため committed 化した op があればゴーストを破棄する。
          applied.forEach(clearTransientForCommittedOp);
          setStatus("ready");
        } else {
          // 参照専用の埋め込み: 取得失敗を空キャンバス成功に変換しない。error として扱い、
          // 呼び出し側で「表示できませんでした」表示へ遷移させる（空キャンバスを見せない）。
          console.warn("[bootstrap] 取得失敗（埋め込み・error として扱う）", e);
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canvasApi, canvasId, loadSnapshot, lazyCreateOnError, createName]);

  return { status };
}
